import React, { useState, useRef } from "react";
import Papa from "papaparse";
import { Upload, FileText, Download, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

const REQUIRED_HEADERS = ["name", "email", "phone", "notes", "fitness_goal"];
// Optional headers — if present we map them, if missing we import the rest.
const OPTIONAL_HEADERS = ["weight_kg", "height_cm"];
const SAMPLE_CSV =
  "name,email,phone,notes,fitness_goal,weight_kg,height_cm\n" +
  "Alice Nguyen,alice@example.com,0412 345 678,Prefers 6am sessions,Marathon PB,62,168\n" +
  "Bob Smith,bob.smith@example.com,0400 111 222,Recovering from knee injury,Strength & mobility,,180\n" +
  "Chloe Wilson,chloe@example.com,,,Weight loss + tone,,\n";

const importButtonLabel = (uploading, rows) => {
  if (uploading) return "Importing...";
  if (rows) return `Import ${rows.length} client${rows.length === 1 ? "" : "s"}`;
  return "Import";
};

export default function ImportClientsDialog({ open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState(null); // parsed rows or null
  const [fileName, setFileName] = useState("");
  const [headerError, setHeaderError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // {created, skipped, total_rows}

  const reset = () => {
    setRows(null);
    setFileName("");
    setHeaderError("");
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = () => {
    onOpenChange(false);
    // Delay reset so the closing animation doesn't flash empty state
    setTimeout(reset, 200);
  };

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "trainmate_clients_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setHeaderError("");
    setResult(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, "_"),
      complete: (res) => {
        const headers = res.meta.fields || [];
        const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
        if (missing.length) {
          setHeaderError(
            `Missing required column${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}. Download the template to see the expected format.`,
          );
          setRows(null);
          return;
        }
        const cleaned = (res.data || []).map((r) => {
          const w = r.weight_kg == null ? "" : String(r.weight_kg).trim();
          const h = r.height_cm == null ? "" : String(r.height_cm).trim();
          const wNum = w === "" ? null : Number(w);
          const hNum = h === "" ? null : Number(h);
          return {
            name: (r.name || "").toString().trim(),
            email: (r.email || "").toString().trim(),
            phone: (r.phone || "").toString().trim(),
            notes: (r.notes || "").toString().trim(),
            fitness_goal: (r.fitness_goal || "").toString().trim(),
            weight_kg: Number.isFinite(wNum) ? wNum : null,
            height_cm: Number.isFinite(hNum) ? hNum : null,
          };
        });
        if (cleaned.length === 0) {
          setHeaderError("The CSV appears to be empty (no data rows).");
          setRows(null);
          return;
        }
        if (cleaned.length > 1000) {
          setHeaderError("Maximum 1,000 rows per import. Please split your file.");
          setRows(null);
          return;
        }
        setRows(cleaned);
      },
      error: (err) => {
        setHeaderError(`Could not parse CSV: ${err.message}`);
        setRows(null);
      },
    });
  };

  const doImport = async () => {
    if (!rows) return;
    setUploading(true);
    try {
      const { data } = await api.post("/clients/import", { rows });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} ${data.created === 1 ? "client" : "clients"}`);
        onImported?.();
      } else {
        toast.warning("No clients were imported");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Import failed";
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent
        className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623] max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="import-clients-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#0F6B6B]" /> Import clients
          </DialogTitle>
          <DialogDescription className="sr-only">
            Bulk-import your existing clients from a CSV file with columns: name, email, phone, notes, fitness_goal.
          </DialogDescription>
        </DialogHeader>

        {/* Result state */}
        {result ? (
          <div className="space-y-5" data-testid="import-result">
            <div className="tm-card p-5 border-l-4 border-[#0F6B6B]">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#0F6B6B] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-display text-xl font-black uppercase">
                    <span className="text-[#0F6B6B]">{result.created}</span> imported
                    {result.skipped_count > 0 && (
                      <span className="text-[#6B635C] text-base font-normal ml-2">
                        · {result.skipped_count} skipped
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-[#6B635C] mt-1">
                    Processed {result.total_rows} row{result.total_rows === 1 ? "" : "s"} from{" "}
                    <span className="text-[#2A2623]">{fileName}</span>.
                  </p>
                </div>
              </div>
            </div>

            {result.skipped_count > 0 && (
              <div data-testid="import-skipped-list">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#7A5E18] font-semibold mb-3 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Skipped rows
                </p>
                <div className="border border-[#E8E1D6] rounded-md divide-y divide-[#E8E1D6] max-h-56 overflow-y-auto">
                  {result.skipped.map((s) => (
                    <div key={`skip-${s.row}`} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                      <span className="text-[10px] font-mono text-[#6B635C] w-14 flex-shrink-0">Row {s.row}</span>
                      <span className="flex-1 text-[#2A2623] truncate">
                        {s.name || <span className="italic text-[#6B635C]">(no name)</span>}
                        {s.email && <span className="text-[#6B635C] ml-2">· {s.email}</span>}
                      </span>
                      <span className="text-xs text-[#7A5E18] flex-shrink-0">{s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Instructions */}
            <div className="tm-card p-4 space-y-3">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#0F6B6B] font-semibold">Expected format</p>
              <p className="text-sm text-[#2A2623] leading-relaxed">
                CSV file with a header row and these <strong>exact</strong> column names (order doesn't matter, case-insensitive):
              </p>
              <div className="flex flex-wrap gap-1.5">
                {REQUIRED_HEADERS.map((h) => (
                  <span key={h} className="text-[11px] font-mono px-2 py-1 rounded bg-[#FAF7F2] border border-[#E8E1D6] text-[#0F6B6B]">
                    {h}
                  </span>
                ))}
              </div>
              <p className="text-xs text-[#6B635C] leading-relaxed">
                Only <span className="font-mono text-[#0F6B6B]">name</span> is required per row. Rows with an email that already exists in your account are automatically skipped. Max 1,000 rows.
              </p>
              <button
                type="button"
                onClick={downloadTemplate}
                data-testid="import-download-template"
                className="inline-flex items-center gap-1.5 text-xs text-[#0F6B6B] hover:underline"
              >
                <Download className="w-3.5 h-3.5" /> Download template CSV
              </button>
            </div>

            {/* File picker */}
            <div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFile}
                className="hidden"
                data-testid="import-file-input"
              />
              {!fileName ? (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="import-pick-file"
                  className="w-full rounded-md border-2 border-dashed border-[#E8E1D6] hover:border-[#0F6B6B] hover:bg-[#FAF7F2] transition-colors p-8 flex flex-col items-center gap-2 text-[#6B635C] hover:text-[#2A2623]"
                >
                  <Upload className="w-6 h-6" />
                  <span className="text-sm font-semibold">Click to choose a CSV file</span>
                  <span className="text-xs text-[#6B635C]">or drop it in your file explorer</span>
                </button>
              ) : (
                <div className="rounded-md border border-[#E8E1D6] bg-[#FAF7F2] px-4 py-3 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#0F6B6B]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" data-testid="import-filename">{fileName}</p>
                    {rows && (
                      <p className="text-xs text-[#6B635C]">{rows.length} row{rows.length === 1 ? "" : "s"} ready to import</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="import-clear-file"
                    className="text-[#6B635C] hover:text-[#2A2623] transition-colors"
                    aria-label="Clear file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {headerError && (
              <div className="rounded-md border border-[#E7B9B2] bg-[#F9E4DF] px-4 py-3 text-sm text-[#A8372A] flex items-start gap-2" data-testid="import-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{headerError}</span>
              </div>
            )}

            {/* Preview */}
            {rows && rows.length > 0 && (
              <div data-testid="import-preview">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B635C] font-semibold mb-2">
                  Preview · showing first {Math.min(5, rows.length)} of {rows.length}
                </p>
                <div className="border border-[#E8E1D6] rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FAF7F2] text-[#6B635C]">
                      <tr>
                        {REQUIRED_HEADERS.map((h) => (
                          <th key={h} className="px-3 py-2 text-left font-mono font-normal">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8E1D6]">
                      {rows.slice(0, 5).map((r, i) => (
                        <tr key={`preview-${i}-${r.email || r.name}`} className="text-[#2A2623]">
                          {REQUIRED_HEADERS.map((h) => (
                            <td key={h} className="px-3 py-2 max-w-[8rem] truncate" title={r[h]}>
                              {r[h] || <span className="text-[#6B635C]">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button
              onClick={close}
              data-testid="import-done"
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold"
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={close}
                data-testid="import-cancel"
                className="border-[#E8E1D6] bg-transparent hover:bg-[#FAF7F2]"
              >
                Cancel
              </Button>
              <Button
                onClick={doImport}
                disabled={!rows || uploading || !!headerError}
                data-testid="import-confirm"
                className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold"
              >
                {importButtonLabel(uploading, rows)}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
